import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {UrlUtils} from '../utils/url-utils';
import {NotificationService} from "../notifications/notification.service";
import {Account, Event, FilterQuery, Log, PageContent, Participant, TagBib, User} from "../../domain/models";

@Injectable({
  providedIn: 'root'
})
export class OpenbandService {

  private baseUrl = UrlUtils.getProdServer();
  private SLASH = '/';
  private EVENTS = 'events';
  private PARTICIPANTS = 'participants';
  private ACCOUNT = 'account';
  private LOGS = 'logs';
  private URI_EVENTS = this.baseUrl + this.SLASH + this.EVENTS;
  private URI_USERS = this.baseUrl + this.SLASH + "users";
  private URI_LOGS = this.baseUrl + this.SLASH + this.LOGS;
  private URI_LOGS_NEW = this.baseUrl + this.SLASH + "logs-new";
  private URI_ACCOUNT = this.baseUrl + this.SLASH + this.ACCOUNT;
  private URI_PARTICIPANTS = this.baseUrl + this.SLASH + this.PARTICIPANTS;

  constructor(
    private http: HttpClient,
    private notifyService: NotificationService
  ) {
  }

  //Events
  getEvents(): Observable<Event[]> {
    return this.http.get<Event[]>(this.URI_EVENTS)
  }

  getEventsAll(): Observable<Event[]> {
    return this.http.get<Event[]>(this.URI_EVENTS+"-all")
  }

  getEvent(id: string): Observable<Event> {
    return this.http.get<Event>(this.URI_EVENTS + this.SLASH + id)
  }

  createEvent(event: Event): Observable<Event> {
    return this.http.post<Event>(this.URI_EVENTS, event)
  }

  getAccountSettings(): Observable<Account> {
    return this.http.get<Account>(this.URI_ACCOUNT)
  }


  //Participants
  getParticipants(eventId: string): Observable<Participant[]> {
    return this.http.get<Participant[]>(this.URI_EVENTS + this.SLASH + eventId + this.SLASH + "participants")
  }

  downloadParticipants(eventId: string): Observable<Participant[]> {
    return this.http.get<Participant[]>(this.URI_EVENTS + this.SLASH + eventId + this.SLASH + "participants/download")
  }

  getTagBibs(eventId: string): Observable<TagBib[]> {
    return this.http.get<TagBib[]>(this.URI_EVENTS + this.SLASH + eventId + this.SLASH + "tag-bib")
  }

  uploadTagBib(eventId: string, list: TagBib[]): Observable<MessageResponse> {
    return this.http.post<MessageResponse>(this.URI_EVENTS + this.SLASH + eventId + this.SLASH + "tag-bib", list)
  }

  assignCategories(eventId: string): Observable<Participant[]> {
    return this.http.patch<Participant[]>(this.URI_EVENTS + this.SLASH + eventId + this.SLASH + "participants/assign-categories", [])
  }

  updateOnlyParticipant(participant: Participant): Observable<Participant> {
    return this.http.patch<Participant>(this.URI_PARTICIPANTS + this.SLASH + participant.id, participant)
  }

  issueParticipant(participant: Participant): Observable<Participant> {
    return this.http.post<Participant>(this.URI_PARTICIPANTS + this.SLASH + participant.id + "/issue", participant)
  }

  updateParticipant(eventId: string, participant: Participant): Observable<Participant> {
    return this.http.patch<Participant>(this.URI_EVENTS + this.SLASH + eventId + this.SLASH + "participants/" + participant.id, participant)
  }

  assignCategoryForParticipant(eventId: string, participantId: string): Observable<Participant> {
    return this.http.patch<Participant>(this.URI_EVENTS + this.SLASH + eventId + this.SLASH + "participants/" + participantId + "/assign-categories", [])
  }

  transferParticipant(participantId: string, eventId: string): Observable<Participant> {
    return this.http.post<Participant>(this.URI_PARTICIPANTS + this.SLASH + participantId + this.SLASH + "transfer", {
      toEventId: eventId
    })
  }

  uploadParticipants(eventId, participants: Participant[]): Observable<Participant[]> {
    return this.http.post<Participant[]>(this.URI_EVENTS + this.SLASH + eventId + this.SLASH + "participants/upload", participants)
  }

  searchParticipants(eventId: string, query: string): Observable<Participant[]> {
    return this.http.get<Participant[]>(this.URI_EVENTS + this.SLASH + eventId + this.SLASH + "participants/search?query=" + query)
  }

  getLogs(type: string): Observable<Log[]> {
    return this.http.get<Log[]>(this.URI_LOGS)
  }

  getLogsNew(
    filter: FilterQuery = {},
    pageNo: number = 0,
    pageSize: number = 50,
    action: string,
    user: string,
    beginTimestamp: string,
    endTimestamp: string,
    searchQuery: string,
  ): Observable<PageContent> {
    const params = new HttpParams()
      .set('page', pageNo.toString())
      .set('pageSize', pageSize.toString())
      .set('action', action)
      .set('user', user)
      .set('fromTimestamp', beginTimestamp)
      .set('toTimestamp', endTimestamp)
      .set('dataContains', searchQuery)

    return this.http.patch<PageContent>(this.URI_LOGS_NEW, filter, {params});
  }

  getUsers(): Observable<User[]> {
    return this.http.get<User[]>(this.URI_USERS)
  }

  createUser(user: User): Observable<User> {
    return this.http.post<User>(this.URI_USERS, user)
  }
  updateUser(user: User): Observable<User> {
    return this.http.patch<User>(this.URI_USERS + this.SLASH + user.id, user)
  }
  deleteUser(user: User): Observable<MessageResponse> {
    return this.http.delete<MessageResponse>(this.URI_USERS + this.SLASH + user.id)
  }


  // createPunch(punch: Punch): Observable<string> {
  //   return this.http.post<string>(this.URI_PUNCHES, [punch])
  // }

  clearTagBib(eventId: string) {
    return this.http.delete<Log[]>(this.URI_EVENTS + this.SLASH + eventId + "/tag-bib/clear");
  }
  clearTagBibInParticipants(eventId: string) {
    return this.http.delete<Log[]>(this.URI_EVENTS + this.SLASH + eventId + "/participants/clear");
  }
}

export class BaseResponse {
  response: any;
  errors: BaseError[];
}

export class BaseError {
  code: string;
  message: string;
  details: string;
}

export class MessageResponse {
  message: string;
}
